import React from "react"
import { MessageCircle, Search, Sparkles, Users } from "lucide-react"

export function HowItWorks() {
  return (
    <section id="how-it-works" className="py-24 md:py-32 bg-card">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-medium text-forest uppercase tracking-wider mb-4">
            How It Works
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-6 text-balance">
            From word-of-mouth to community intelligence
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Corkboard bridges the gap between traditional local knowledge and modern
            technology, without losing the human touch.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <Step
            number="01"
            icon={<MessageCircle className="w-6 h-6" />}
            title="Share what's on"
            description="Retailers and organisers post events, offers, and news through our simple portal. Or just message our WhatsApp bot."
          />
          <Step
            number="02"
            icon={<Sparkles className="w-6 h-6" />}
            title="AI organises it"
            description="Our responsible AI categorises, validates, and keeps information fresh, automatically removing outdated content."
          />
          <Step
            number="03"
            icon={<Search className="w-6 h-6" />}
            title="Discover locally"
            description="Ask questions in plain language: 'What's happening Saturday?' or 'Where can I find fresh bread nearby?'"
          />
          <Step
            number="04"
            icon={<Users className="w-6 h-6" />}
            title="Community thrives"
            description="More footfall for local businesses, more connection for residents, and a high street that feels alive again."
          />
        </div>

        {/* Visual Flow Line (Desktop) */}
        <div className="hidden lg:block mt-12">
          <div className="relative max-w-4xl mx-auto">
            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-border -translate-y-1/2" />
            <div className="flex justify-between relative">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-4 h-4 rounded-full bg-forest border-4 border-card"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function Step({
  number,
  icon,
  title,
  description,
}: {
  number: string
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <div className="relative group">
      {/* Number badge */}
      <div className="absolute -top-3 -left-3 w-8 h-8 bg-amber text-foreground rounded-full flex items-center justify-center text-xs font-bold z-10">
        {number}
      </div>

      {/* Card */}
      <div className="bg-cream rounded-2xl p-6 h-full border border-border/50 group-hover:border-forest/30 group-hover:shadow-lg transition-all duration-300">
        {/* Icon */}
        <div className="w-14 h-14 rounded-xl bg-forest/10 text-forest flex items-center justify-center mb-5 group-hover:bg-forest group-hover:text-cream transition-colors duration-300">
          {icon}
        </div>

        {/* Content */}
        <h3 className="font-semibold text-lg text-foreground mb-3">{title}</h3>
        <p className="text-muted-foreground leading-relaxed text-sm">{description}</p>
      </div>
    </div>
  )
}
